import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { api } from "~/lib/api.js";

// Define allowed paths for each view mode
// Paths support parameter placeholders like :id, :uuid, etc.
const DTO_ALLOWED_PATHS = [
  "/",
  "/members",
  "/members/:id",
  "/dto",
  "/dto/:id",
  "/email",
  "/news",
];

const ASSOCIATION_ALLOWED_PATHS = [
  "/",
  "/email",
  "/news",
  "/my_details",
  "/documents",
  "/documents/:tabID",
  "/findings",
  "/admin/users",
  "/admin/groups",
  "/admin/competencies",
  "/admin/settings",
];


const ClubContext = createContext({
  selectedDTOClub: null,
  setSelectedDTOClub: (club) => {},
  clubs: [],
  loadingClubs: false,
  isAssociation: false,
  canView: (path) => true,
});

export const ClubProvider = ({ children }) => {
  const [selectedDTOClub, setSelectedDTOClub] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [loadingClubs, setLoadingClubs] = useState(false);

  const { club, profile, isAuthenticated } = useSelector((state) => state.persist);

  const isAssociation = !selectedDTOClub && club?.is_association || false;

  // Only fetch clubs once per session
  useEffect(() => {
    const fetchClubs = async () => {
      setLoadingClubs(true);
      try {
        const result = await api.get("admin/list_association.json");
        if (result) {
          setClubs(result?.clubs || []);
          window.associationKey = result?.association_key || null;
        }
      } finally {
        setLoadingClubs(false);
      }
    };
    isAuthenticated && profile.wijzig_dto && isAssociation && fetchClubs();
  }, [isAssociation, profile.wijzig_dto, isAuthenticated]);

  // View mode access control
  const canView = useCallback((path) => {
    // Club mode: always allow
    if (!selectedDTOClub && !isAssociation) {
      return true;
    }

    // DTO mode: check against DTO allowed list
    if (selectedDTOClub) {
      return DTO_ALLOWED_PATHS.indexOf(path) !== -1;
    }

    // Association mode: check against Association allowed list
    if (isAssociation) {
      return ASSOCIATION_ALLOWED_PATHS.indexOf(path) !== -1;
    }

    return true;
  }, [selectedDTOClub, isAssociation]);

  const contextValue = useMemo(() => ({
    selectedDTOClub,
    setSelectedDTOClub,
    clubs,
    loadingClubs,
    isAssociation,
    canView,
  }), [selectedDTOClub, clubs, loadingClubs, isAssociation, canView]);

  return (
    <ClubContext.Provider value={contextValue}>
      {children}
    </ClubContext.Provider>
  );
};

export const useClub = () => useContext(ClubContext);
